<template>
  <div class="voice-root">
    <!-- 백드롭 -->
    <div class="voice-backdrop" @click.self="onClose"></div>

    <!-- 컨테이너 -->
    <div
      class="voice-container"
      :class="containerClasses"
      :style="containerStyle"
    >
      <!-- 헤더 -->
      <header class="voice-header">
        <div class="voice-header-left">
          <button
            v-if="view !== 'main'"
            class="voice-icon-btn voice-back-btn"
            :title="$t('뒤로')"
            @click="goBack"
          >
            <feather-icon icon="ArrowLeftIcon" class="voice-icon" />
          </button>
          <span v-if="view === 'main'" class="voice-status-dot" :class="statusDotClass"></span>
          <div class="voice-header-title">
            <span class="voice-title">{{ headerTitle }}</span>
            <span v-if="view === 'main'" class="voice-subtitle">{{ statusText }}</span>
          </div>
        </div>
        <div class="voice-header-actions">
          <span v-if="view === 'main' && vmState.isRecording" class="voice-timer" :class="timerClass">{{ formatTime(recordingElapsed) }}</span>
          <button
            v-if="view === 'main' && micPermission === 'granted'"
            class="voice-icon-btn"
            :disabled="!canOpenHistory"
            :title="$t('녹음 히스토리')"
            @click="openHistory"
          >
            <feather-icon icon="ClockIcon" class="voice-icon" />
          </button>
          <button
            v-if="!isMobile && view === 'main'"
            class="voice-icon-btn"
            :title="fullScreen ? $t('축소') : $t('전체화면')"
            @click="fullScreen = !fullScreen"
          >
            <feather-icon :icon="fullScreen ? 'Minimize2Icon' : 'MaximizeIcon'" class="voice-icon" />
          </button>
          <button class="voice-icon-btn" :title="$t('닫기')" @click="onClose">
            <feather-icon icon="XIcon" class="voice-icon" />
          </button>
        </div>
      </header>

      <!-- 마이크 권한 요청 -->
      <section v-if="view === 'main' && micPermission !== 'granted'" class="voice-permission">
        <div class="voice-permission-icon">
          <feather-icon icon="MicOffIcon" />
        </div>
        <template v-if="micPermission === 'checking'">
          <p class="voice-permission-text">{{ $t('마이크 권한 확인 중...') }}</p>
        </template>
        <template v-else-if="micPermission === 'denied'">
          <h3 class="voice-permission-title">{{ $t('마이크 접근이 차단되어 있습니다') }}</h3>
          <p class="voice-permission-text">{{ $t('브라우저 주소창 왼쪽의 자물쇠 아이콘을 클릭하여 마이크 권한을 허용해 주세요') }}</p>
        </template>
        <template v-else>
          <h3 class="voice-permission-title">{{ $t('마이크 권한이 필요합니다') }}</h3>
          <p class="voice-permission-text">{{ $t('실시간 음성 번역을 사용하려면 마이크 접근 권한이 필요합니다') }}</p>
          <button class="voice-btn voice-btn-primary" @click="requestMicPermission">
            <feather-icon icon="MicIcon" class="voice-icon" />
            <span>{{ $t('마이크 권한 허용') }}</span>
          </button>
        </template>
      </section>

      <!-- 제어 영역 -->
      <section v-else-if="view === 'main'" class="voice-controls">
        <!-- 언어 선택기 -->
        <div class="voice-lang-row">
          <!-- 출발 언어 -->
          <div class="voice-lang-picker" :class="{ 'is-open': sourceOpen, 'is-disabled': vmState.isRecording || vmState.isConnecting }">
            <button
              class="voice-lang-btn"
              :disabled="vmState.isRecording || vmState.isConnecting"
              @click="toggleSourceDropdown"
            >
              <span class="voice-lang-flag">{{ currentSourceLang.flag }}</span>
              <span class="voice-lang-name">{{ currentSourceLang.label }}</span>
              <feather-icon icon="ChevronDownIcon" class="voice-lang-caret" />
            </button>
            <div v-if="sourceOpen" class="voice-lang-dropdown" @click.stop>
              <button
                v-for="lang in languages"
                :key="lang.code"
                class="voice-lang-option"
                :class="{ 'is-active': lang.code === sourceLang }"
                @click="selectSource(lang.code)"
              >
                <span class="voice-lang-flag">{{ lang.flag }}</span>
                <div class="voice-lang-option-text">
                  <span class="voice-lang-option-label">{{ lang.label }}</span>
                  <span class="voice-lang-option-sub">{{ lang.english }}</span>
                </div>
                <feather-icon v-if="lang.code === sourceLang" icon="CheckIcon" class="voice-icon voice-lang-check" />
              </button>
            </div>
          </div>

          <!-- Swap 버튼 -->
          <button
            class="voice-swap-btn"
            :class="{ 'is-swapping': swapAnimating }"
            :disabled="vmState.isRecording || vmState.isConnecting"
            :title="$t('언어 교체')"
            @click="swapLanguages"
          >
            <feather-icon icon="RefreshCwIcon" class="voice-icon" />
          </button>

          <!-- 도착 언어 -->
          <div class="voice-lang-picker" :class="{ 'is-open': targetOpen, 'is-disabled': vmState.isRecording || vmState.isConnecting }">
            <button
              class="voice-lang-btn"
              :disabled="vmState.isRecording || vmState.isConnecting"
              @click="toggleTargetDropdown"
            >
              <span class="voice-lang-flag">{{ currentTargetLang.flag }}</span>
              <span class="voice-lang-name">{{ currentTargetLang.label }}</span>
              <feather-icon icon="ChevronDownIcon" class="voice-lang-caret" />
            </button>
            <div v-if="targetOpen" class="voice-lang-dropdown" @click.stop>
              <button
                v-for="lang in languages"
                :key="lang.code"
                class="voice-lang-option"
                :class="{ 'is-active': lang.code === targetLang }"
                @click="selectTarget(lang.code)"
              >
                <span class="voice-lang-flag">{{ lang.flag }}</span>
                <div class="voice-lang-option-text">
                  <span class="voice-lang-option-label">{{ lang.label }}</span>
                  <span class="voice-lang-option-sub">{{ lang.english }}</span>
                </div>
                <feather-icon v-if="lang.code === targetLang" icon="CheckIcon" class="voice-icon voice-lang-check" />
              </button>
            </div>
          </div>
        </div>

        <!-- 컨트롤 버튼 -->
        <div class="voice-action-row">
          <button
            v-if="!vmState.isRecording && !vmState.isConnecting"
            class="voice-btn voice-btn-primary voice-btn-main"
            @click="handleStart"
          >
            <feather-icon icon="MicIcon" class="voice-icon" />
            <span>{{ $t('녹음 시작') }}</span>
          </button>
          <template v-else>
            <button
              v-if="vmState.isRecording && !isPaused"
              class="voice-btn voice-btn-outline"
              @click="handlePause"
            >
              <feather-icon icon="PauseIcon" class="voice-icon" />
              <span>{{ $t('일시정지') }}</span>
            </button>
            <button
              v-else-if="vmState.isRecording && isPaused"
              class="voice-btn voice-btn-outline"
              @click="handleResume"
            >
              <feather-icon icon="PlayIcon" class="voice-icon" />
              <span>{{ $t('재개') }}</span>
            </button>
            <button
              class="voice-btn voice-btn-danger voice-btn-main"
              :disabled="isStopping"
              @click="handleStop"
            >
              <feather-icon v-if="isStopping" icon="LoaderIcon" class="voice-icon voice-spin" />
              <feather-icon v-else icon="SquareIcon" class="voice-icon" />
              <span v-if="isStopping">{{ $t('결과 수신 중...') }}</span>
              <span v-else>{{ $t('정지') }}</span>
            </button>
          </template>
          <button
            class="voice-icon-btn voice-action-reset"
            :disabled="vmState.isRecording || vmState.isConnecting || isStopping || isEmpty"
            :title="$t('초기화')"
            @click="handleReset"
          >
            <feather-icon icon="RotateCcwIcon" class="voice-icon" />
          </button>
        </div>

        <!-- 오류 알림 (재연결 옵션 포함) -->
        <div v-if="vmState.error" class="voice-error">
          <feather-icon icon="AlertCircleIcon" class="voice-icon" />
          <span class="voice-error-text">{{ vmState.error }}</span>
          <button
            v-if="canRetry"
            class="voice-error-retry"
            @click="handleStart"
          >{{ $t('다시 연결') }}</button>
        </div>
      </section>

      <!-- 결과 영역 -->
      <section v-if="view === 'main' && micPermission === 'granted'" class="voice-result">
        <!-- 빈 상태 카드 -->
        <div v-if="isEmpty && !vmState.isRecording && !vmState.isConnecting" class="voice-empty">
          <div class="voice-empty-icon">
            <feather-icon icon="MicIcon" />
          </div>
          <h3 class="voice-empty-title">{{ $t('녹음 준비 완료') }}</h3>
          <p class="voice-empty-desc">{{ $t('녹음 시작 버튼을 눌러 음성 통역을 시작하세요') }}</p>
          <div class="voice-empty-chip">
            <span class="voice-lang-flag">{{ currentSourceLang.flag }}</span>
            <span>{{ currentSourceLang.label }}</span>
            <feather-icon icon="ArrowRightIcon" class="voice-icon voice-empty-arrow" />
            <span class="voice-lang-flag">{{ currentTargetLang.flag }}</span>
            <span>{{ currentTargetLang.label }}</span>
          </div>
          <p class="voice-empty-policy">
            <feather-icon icon="InfoIcon" class="voice-icon voice-empty-policy-icon" />
            {{ $t('최대 {minutes}분 녹음 · {seconds}초 무음 시 자동 종료', { minutes: MAX_SESSION_MIN, seconds: IDLE_TIMEOUT_SEC }) }}
          </p>
        </div>

        <!-- 카드 리스트 (VAD 단위 발화) -->
        <div v-else class="voice-turns-wrap">
          <!-- 세션 종료 배너 -->
          <div v-if="sessionSummary && !vmState.isRecording" class="voice-summary-banner">
            <feather-icon icon="CheckCircleIcon" class="voice-icon voice-summary-banner-check" />
            <div class="voice-summary-banner-text">
              <span class="voice-summary-banner-title">
                {{ $t('세션 종료') }}
                <span v-if="autoStopReasonText" class="voice-summary-banner-reason">· {{ autoStopReasonText }}</span>
              </span>
              <span class="voice-summary-banner-meta">
                {{ $t('총 n건', { count: vmState.turns.length }) }} · {{ formatTime(lastSessionDuration) }}
              </span>
            </div>
            <button
              v-if="lastRecordingId"
              class="voice-summary-banner-link"
              @click="openHistoryDetail(lastRecordingId)"
            >
              <feather-icon icon="ExternalLinkIcon" class="voice-icon" />
              <span>{{ $t('녹음 보기') }}</span>
            </button>
            <button
              class="voice-icon-btn voice-summary-banner-action"
              :title="$t('전체 복사')"
              @click="copyAllTurns"
            >
              <feather-icon icon="CopyIcon" class="voice-icon" />
            </button>
          </div>

          <!-- 카드 스크롤 영역 (최신 카드가 상단) -->
          <div ref="turnsList" class="voice-turns-list" @scroll="onTurnsScroll">
            <!-- 진행 중 partial 라이브 카드 — 최상단 -->
            <article
              v-if="vmState.previewText || vmState.previewTranslation"
              class="voice-turn-card voice-turn-card-live"
            >
              <header class="voice-turn-header">
                <span class="voice-turn-time voice-turn-live-badge">
                  <span class="voice-live-dot"></span>{{ $t('실시간') }}
                </span>
              </header>
              <div class="voice-turn-row">
                <span class="voice-turn-chip">
                  <span class="voice-lang-flag">{{ currentSourceLang.flag }}</span>
                  <span>{{ currentSourceLang.label }}</span>
                </span>
                <p class="voice-turn-text voice-turn-partial">
                  {{ vmState.previewText }}<span class="voice-cursor">▍</span>
                </p>
              </div>
              <div class="voice-turn-divider"></div>
              <div class="voice-turn-row voice-turn-row-target">
                <span class="voice-turn-chip">
                  <span class="voice-lang-flag">{{ currentTargetLang.flag }}</span>
                  <span>{{ currentTargetLang.label }}</span>
                </span>
                <p class="voice-turn-text voice-turn-partial">
                  {{ vmState.previewTranslation }}<span v-if="vmState.previewTranslation" class="voice-cursor">▍</span>
                </p>
              </div>
            </article>

            <!-- 확정된 카드들 (최신순) -->
            <article
              v-for="(turn, idx) in reversedTurns"
              :key="turn.id"
              class="voice-turn-card"
            >
              <header class="voice-turn-header">
                <span class="voice-turn-time">{{ turn.time }}</span>
                <span class="voice-turn-index">#{{ vmState.turns.length - idx }}</span>
                <button class="voice-icon-btn voice-turn-action" :title="$t('복사')" @click="copyTurn(turn)">
                  <feather-icon icon="CopyIcon" class="voice-icon" />
                </button>
              </header>

              <div class="voice-turn-row">
                <span class="voice-turn-chip">
                  <span class="voice-lang-flag">{{ flagOf(turn.sourceLang) }}</span>
                  <span>{{ labelOf(turn.sourceLang) }}</span>
                </span>
                <p class="voice-turn-text">{{ turn.original }}</p>
                <button
                  v-if="turn.original"
                  class="voice-icon-btn voice-turn-row-action"
                  :class="{ 'is-active': isPlayingKey(turn.id + '_src') || isLoadingKey(turn.id + '_src') }"
                  :title="isPlayingKey(turn.id + '_src') ? $t('정지') : $t('음성 재생')"
                  @click="speakText(turn.original, turn.sourceLang, turn.id + '_src')"
                >
                  <feather-icon v-if="isLoadingKey(turn.id + '_src')" icon="LoaderIcon" class="voice-icon voice-spin" />
                  <feather-icon v-else-if="isPlayingKey(turn.id + '_src')" icon="SquareIcon" class="voice-icon" />
                  <feather-icon v-else icon="Volume2Icon" class="voice-icon" />
                </button>
              </div>

              <div class="voice-turn-divider"></div>

              <div class="voice-turn-row voice-turn-row-target">
                <span class="voice-turn-chip">
                  <span class="voice-lang-flag">{{ flagOf(turn.targetLang) }}</span>
                  <span>{{ labelOf(turn.targetLang) }}</span>
                </span>
                <p class="voice-turn-text">{{ turn.translated }}</p>
                <button
                  v-if="turn.translated"
                  class="voice-icon-btn voice-turn-row-action"
                  :class="{ 'is-active': isPlayingKey(turn.id + '_tgt') || isLoadingKey(turn.id + '_tgt') }"
                  :title="isPlayingKey(turn.id + '_tgt') ? $t('정지') : $t('음성 재생')"
                  @click="speakText(turn.translated, turn.targetLang, turn.id + '_tgt')"
                >
                  <feather-icon v-if="isLoadingKey(turn.id + '_tgt')" icon="LoaderIcon" class="voice-icon voice-spin" />
                  <feather-icon v-else-if="isPlayingKey(turn.id + '_tgt')" icon="SquareIcon" class="voice-icon" />
                  <feather-icon v-else icon="Volume2Icon" class="voice-icon" />
                </button>
              </div>
            </article>
          </div>

          <!-- 새 메시지 배지 (사용자가 아래로 스크롤한 상태일 때만) -->
          <button
            v-if="userScrolledDown && newTurnBadge > 0"
            class="voice-new-badge"
            @click="scrollTurnsToTop(true)"
          >
            <feather-icon icon="ArrowUpIcon" class="voice-icon" />
            <span>{{ $t('새 메시지 n건', { count: newTurnBadge }) }}</span>
          </button>
        </div>
      </section>

      <!-- 히스토리 목록 뷰 -->
      <section v-if="view === 'history-list'" class="voice-history">
        <!-- 로딩 -->
        <div v-if="historyLoading && historyList.length === 0" class="voice-history-loading">
          <div v-for="i in 3" :key="i" class="voice-history-skeleton">
            <div class="voice-skeleton-line voice-skeleton-line-1"></div>
            <div class="voice-skeleton-line voice-skeleton-line-2"></div>
            <div class="voice-skeleton-line voice-skeleton-line-3"></div>
          </div>
        </div>

        <!-- 에러 -->
        <div v-else-if="historyError && historyList.length === 0" class="voice-history-error">
          <feather-icon icon="AlertCircleIcon" class="voice-icon voice-history-error-icon" />
          <p class="voice-history-error-text">{{ historyError }}</p>
          <button class="voice-btn voice-btn-outline" @click="loadHistoryList(1)">
            <feather-icon icon="RefreshCwIcon" class="voice-icon" />
            <span>{{ $t('다시 시도') }}</span>
          </button>
        </div>

        <!-- 빈 상태 -->
        <div v-else-if="!historyLoading && historyList.length === 0" class="voice-history-empty">
          <div class="voice-history-empty-icon">
            <feather-icon icon="ClockIcon" />
          </div>
          <h3 class="voice-history-empty-title">{{ $t('아직 녹음 기록이 없습니다') }}</h3>
          <p class="voice-history-empty-desc">{{ $t('녹음을 시작하면 이곳에 자동으로 저장됩니다') }}</p>
        </div>

        <!-- 목록 -->
        <div v-else class="voice-history-content">
          <div class="voice-history-meta">
            <span>{{ $t('총 n건', { count: historyTotal }) }}</span>
          </div>

          <div class="voice-history-cards">
            <article
              v-for="rec in historyList"
              :key="rec.recording_id"
              class="voice-history-card"
              @click="openHistoryDetail(rec.recording_id)"
            >
              <header class="voice-history-card-header">
                <div class="voice-history-card-time">
                  <feather-icon icon="CalendarIcon" class="voice-icon voice-history-card-icon" />
                  <span>{{ formatHistoryTime(rec.started_at) }}</span>
                </div>
                <span class="voice-history-card-duration">
                  <feather-icon icon="ClockIcon" class="voice-icon voice-history-card-icon" />
                  {{ formatHistoryDuration(rec) }}
                </span>
              </header>

              <div class="voice-history-card-langs">
                <span class="voice-history-lang">
                  <span class="voice-lang-flag">{{ flagOf(rec.source_language) }}</span>
                  <span>{{ labelOf(rec.source_language) }}</span>
                </span>
                <feather-icon icon="ArrowRightIcon" class="voice-icon voice-history-arrow" />
                <span class="voice-history-lang">
                  <span class="voice-lang-flag">{{ flagOf(rec.target_language) }}</span>
                  <span>{{ labelOf(rec.target_language) }}</span>
                </span>
                <span class="voice-history-turn-badge">
                  <feather-icon icon="MessageSquareIcon" class="voice-icon voice-history-card-icon" />
                  {{ rec.turn_count }}
                </span>
              </div>

              <div v-if="rec.preview_stt || rec.preview_translation" class="voice-history-preview">
                <p v-if="rec.preview_stt" class="voice-history-preview-line">{{ rec.preview_stt }}</p>
                <p v-if="rec.preview_translation" class="voice-history-preview-line voice-history-preview-target">{{ rec.preview_translation }}</p>
              </div>
            </article>
          </div>

          <!-- 페이지네이션 -->
          <div v-if="historyTotalPages > 1" class="voice-history-pagination">
            <button
              class="voice-icon-btn"
              :disabled="historyPage <= 1 || historyLoading"
              :title="$t('이전')"
              @click="loadHistoryList(historyPage - 1)"
            >
              <feather-icon icon="ChevronLeftIcon" class="voice-icon" />
            </button>
            <span class="voice-history-page-info">{{ historyPage }} / {{ historyTotalPages }}</span>
            <button
              class="voice-icon-btn"
              :disabled="historyPage >= historyTotalPages || historyLoading"
              :title="$t('다음')"
              @click="loadHistoryList(historyPage + 1)"
            >
              <feather-icon icon="ChevronRightIcon" class="voice-icon" />
            </button>
          </div>
        </div>
      </section>

      <!-- 히스토리 상세 뷰 -->
      <section v-if="view === 'history-detail'" class="voice-history">
        <!-- 로딩 -->
        <div v-if="detailLoading" class="voice-history-loading">
          <div class="voice-history-skeleton voice-history-skeleton-detail-header">
            <div class="voice-skeleton-line voice-skeleton-line-1"></div>
            <div class="voice-skeleton-line voice-skeleton-line-2"></div>
          </div>
          <div v-for="i in 3" :key="i" class="voice-history-skeleton">
            <div class="voice-skeleton-line voice-skeleton-line-1"></div>
            <div class="voice-skeleton-line voice-skeleton-line-3"></div>
          </div>
        </div>

        <!-- 에러 -->
        <div v-else-if="detailError" class="voice-history-error">
          <feather-icon icon="AlertCircleIcon" class="voice-icon voice-history-error-icon" />
          <p class="voice-history-error-text">{{ detailError }}</p>
          <button class="voice-btn voice-btn-outline" @click="goBack">
            <feather-icon icon="ArrowLeftIcon" class="voice-icon" />
            <span>{{ $t('목록으로') }}</span>
          </button>
        </div>

        <!-- 상세 본문 -->
        <div v-else-if="detailData" class="voice-history-content">
          <!-- 상세 헤더 -->
          <div class="voice-history-detail-header">
            <div class="voice-history-detail-meta">
              <span class="voice-history-detail-time">
                <feather-icon icon="CalendarIcon" class="voice-icon voice-history-card-icon" />
                {{ formatHistoryTime(detailData.started_at) }}
              </span>
              <span class="voice-history-detail-divider">·</span>
              <span class="voice-history-detail-duration">
                <feather-icon icon="ClockIcon" class="voice-icon voice-history-card-icon" />
                {{ formatHistoryDuration(detailData) }}
              </span>
              <span class="voice-history-detail-divider">·</span>
              <span>
                <feather-icon icon="MessageSquareIcon" class="voice-icon voice-history-card-icon" />
                {{ $t('총 n건', { count: detailData.turn_count }) }}
              </span>
            </div>
            <div class="voice-history-detail-langs">
              <span class="voice-history-lang">
                <span class="voice-lang-flag">{{ flagOf(detailData.source_language) }}</span>
                <span>{{ labelOf(detailData.source_language) }}</span>
              </span>
              <feather-icon icon="ArrowRightIcon" class="voice-icon voice-history-arrow" />
              <span class="voice-history-lang">
                <span class="voice-lang-flag">{{ flagOf(detailData.target_language) }}</span>
                <span>{{ labelOf(detailData.target_language) }}</span>
              </span>
              <button
                class="voice-icon-btn voice-history-detail-copy"
                :title="$t('전체 복사')"
                @click="copyDetailAll"
              >
                <feather-icon icon="CopyIcon" class="voice-icon" />
              </button>
            </div>
          </div>

          <!-- turn 카드 리스트 -->
          <div class="voice-history-turns">
            <article
              v-for="(turn, idx) in detailData.turns"
              :key="'d_' + turn.chunk_index + '_' + idx"
              class="voice-turn-card"
            >
              <header class="voice-turn-header">
                <span class="voice-turn-time">{{ formatTurnTime(turn.created_at) }}</span>
                <span class="voice-turn-index">#{{ idx + 1 }}</span>
                <span v-if="turn.audio_duration_seconds" class="voice-turn-duration">
                  {{ turn.audio_duration_seconds.toFixed(1) }}s
                </span>
                <button
                  class="voice-icon-btn voice-turn-action"
                  :title="$t('복사')"
                  @click="copyDetailTurn(turn)"
                >
                  <feather-icon icon="CopyIcon" class="voice-icon" />
                </button>
              </header>

              <div v-if="turn.stt_text" class="voice-turn-row">
                <span class="voice-turn-chip">
                  <span class="voice-lang-flag">{{ flagOf(turn.source_language || detailData.source_language) }}</span>
                  <span>{{ labelOf(turn.source_language || detailData.source_language) }}</span>
                </span>
                <p class="voice-turn-text">{{ turn.stt_text }}</p>
                <button
                  class="voice-icon-btn voice-turn-row-action"
                  :class="{ 'is-active': isPlayingKey('d_' + idx + '_src') || isLoadingKey('d_' + idx + '_src') }"
                  :title="isPlayingKey('d_' + idx + '_src') ? $t('정지') : $t('음성 재생')"
                  @click="speakText(turn.stt_text, turn.source_language || detailData.source_language, 'd_' + idx + '_src')"
                >
                  <feather-icon v-if="isLoadingKey('d_' + idx + '_src')" icon="LoaderIcon" class="voice-icon voice-spin" />
                  <feather-icon v-else-if="isPlayingKey('d_' + idx + '_src')" icon="SquareIcon" class="voice-icon" />
                  <feather-icon v-else icon="Volume2Icon" class="voice-icon" />
                </button>
              </div>

              <div v-if="turn.stt_text && turn.translated_text" class="voice-turn-divider"></div>

              <div v-if="turn.translated_text" class="voice-turn-row voice-turn-row-target">
                <span class="voice-turn-chip">
                  <span class="voice-lang-flag">{{ flagOf(turn.target_language || detailData.target_language) }}</span>
                  <span>{{ labelOf(turn.target_language || detailData.target_language) }}</span>
                </span>
                <p class="voice-turn-text">{{ turn.translated_text }}</p>
                <button
                  class="voice-icon-btn voice-turn-row-action"
                  :class="{ 'is-active': isPlayingKey('d_' + idx + '_tgt') || isLoadingKey('d_' + idx + '_tgt') }"
                  :title="isPlayingKey('d_' + idx + '_tgt') ? $t('정지') : $t('음성 재생')"
                  @click="speakText(turn.translated_text, turn.target_language || detailData.target_language, 'd_' + idx + '_tgt')"
                >
                  <feather-icon v-if="isLoadingKey('d_' + idx + '_tgt')" icon="LoaderIcon" class="voice-icon voice-spin" />
                  <feather-icon v-else-if="isPlayingKey('d_' + idx + '_tgt')" icon="SquareIcon" class="voice-icon" />
                  <feather-icon v-else icon="Volume2Icon" class="voice-icon" />
                </button>
              </div>
            </article>
          </div>
        </div>
      </section>

      <!-- 토스트 -->
      <transition name="voice-toast">
        <div v-if="toastMessage" class="voice-toast">
          <feather-icon icon="CheckIcon" class="voice-icon" />
          <span>{{ toastMessage }}</span>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import { useVoiceRealtimeTranslate } from '@/composables/useVoiceRealtimeTranslate'

const LANGUAGES = [
  { code: 'ko', label: '한국어',           english: 'Korean',     flag: '🇰🇷', bcp47: 'ko-KR' },
  { code: 'en', label: 'English',          english: 'English',    flag: '🇺🇸', bcp47: 'en-US' },
  { code: 'zh', label: '中文',             english: 'Chinese',    flag: '🇨🇳', bcp47: 'zh-CN' },
  { code: 'ja', label: '日本語',            english: 'Japanese',   flag: '🇯🇵', bcp47: 'ja-JP' },
  { code: 'vi', label: 'Tiếng Việt',       english: 'Vietnamese', flag: '🇻🇳', bcp47: 'vi-VN' },
  { code: 'id', label: 'Bahasa Indonesia', english: 'Indonesian', flag: '🇮🇩', bcp47: 'id-ID' },
  { code: 'th', label: 'ภาษาไทย',          english: 'Thai',       flag: '🇹🇭', bcp47: 'th-TH' },
  { code: 'mn', label: 'Монгол',           english: 'Mongolian',  flag: '🇲🇳', bcp47: 'mn-MN' },
  { code: 'uz', label: "O'zbekcha",        english: 'Uzbek',      flag: '🇺🇿', bcp47: 'uz-UZ' },
  { code: 'my', label: 'မြန်မာ',           english: 'Burmese',    flag: '🇲🇲', bcp47: 'my-MM' },
  { code: 'km', label: 'ភាសាខ្មែរ',         english: 'Khmer',      flag: '🇰🇭', bcp47: 'km-KH' },
  { code: 'ne', label: 'नेपाली',           english: 'Nepali',     flag: '🇳🇵', bcp47: 'ne-NP' },
]

export default {
  name: 'AiVoiceRealtimeTranslate',

  data() {
    const voiceApi = useVoiceRealtimeTranslate()
    return {
      voiceApi,
      vmState: {
        isRecording:        false,
        isConnecting:       false,
        liveReady:          false,
        sttText:            '',
        translatedText:     '',
        previewText:        '',
        previewTranslation: '',
        turns:              [],
        autoStopReason:     null,
        error:              null,
        sessionId:          null,
        recordingId:        null,
      },

      MAX_SESSION_MIN: 15,
      IDLE_TIMEOUT_SEC: 60,

      // 히스토리 뷰
      view: 'main',  // 'main' | 'history-list' | 'history-detail'
      historyList:       [],
      historyTotal:      0,
      historyPage:       1,
      historyLimit:      20,
      historyTotalPages: 0,
      historyLoading:    false,
      historyError:      null,

      detailData:    null,
      detailLoading: false,
      detailError:   null,

      lastRecordingId: null,  // 직전 세션의 recording_id (방금 녹음 보기용)

      sourceLang: 'ko',
      targetLang: 'en',
      languages:  LANGUAGES,

      micPermission: 'checking',
      isPaused:    false,
      isStopping:  false,
      fullScreen:  false,

      sourceOpen:    false,
      targetOpen:    false,
      swapAnimating: false,

      sessionSummary:      null,
      lastSessionDuration: 0,

      userScrolledDown: false,
      newTurnBadge:     0,

      browserVoices:      [],
      ttsKey:             null,    // 현재 재생/로딩 중인 버튼 식별자
      ttsStatus:          'idle',  // 'idle' | 'loading' | 'playing'
      ttsAudio:           null,    // 서버 TTS Audio 인스턴스
      ttsAudioBlobUrl:    null,
      ttsAbortController: null,

      recordingStartTime: 0,
      recordingElapsed:   0,
      timerInterval:      null,

      toastMessage: '',
      toastTimer:   null,

      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
    }
  },

  computed: {
    isMobile() {
      return this.windowWidth <= 768
    },
    currentSourceLang() {
      var found = this.languages.find((l) => l.code === this.sourceLang)
      return found ? found : this.languages[0]
    },
    currentTargetLang() {
      var found = this.languages.find((l) => l.code === this.targetLang)
      return found ? found : this.languages[1]
    },
    isEmpty() {
      var hasTurns = this.vmState.turns && this.vmState.turns.length > 0
      return !hasTurns && !this.vmState.previewText && !this.vmState.previewTranslation
    },
    hasTurns() {
      return this.vmState.turns && this.vmState.turns.length > 0
    },
    reversedTurns() {
      // 최신 카드가 위에 오도록 뒤집기 (원본 배열은 보존)
      return this.vmState.turns.slice().reverse()
    },
    langByCode() {
      var map = {}
      this.languages.forEach(function(l) { map[l.code] = l })
      return map
    },
    summaryStt() {
      // 서버 응답이 비어있으면 누적 STT로 폴백
      if (this.sessionSummary && this.sessionSummary.full_stt) {
        return this.sessionSummary.full_stt
      }
      return this.vmState.sttText
    },
    summaryTranslation() {
      if (this.sessionSummary && this.sessionSummary.full_translation) {
        return this.sessionSummary.full_translation
      }
      return this.vmState.translatedText
    },
    canRetry() {
      if (!this.vmState.error) return false
      if (this.vmState.isRecording || this.vmState.isConnecting) return false
      return /WebSocket|연결|network/i.test(this.vmState.error)
    },
    statusDotClass() {
      if (this.vmState.error)                          return 'is-error'
      if (this.vmState.isConnecting)                   return 'is-connecting'
      if (this.vmState.isRecording && this.vmState.liveReady && !this.isPaused) return 'is-recording'
      if (this.vmState.isRecording && this.isPaused)   return 'is-paused'
      if (this.vmState.isRecording)                    return 'is-preparing'
      return 'is-idle'
    },
    sessionRemainingSec() {
      // 헤더 타이머 색상 결정용 — 남은 시간(초)
      var maxSec = this.MAX_SESSION_MIN * 60
      var remain = maxSec - this.recordingElapsed
      return remain > 0 ? remain : 0
    },
    timerClass() {
      if (!this.vmState.isRecording) return ''
      if (this.sessionRemainingSec <= 30) return 'is-critical'
      if (this.sessionRemainingSec <= 120) return 'is-warning'
      return ''
    },
    autoStopReasonText() {
      var r = this.vmState.autoStopReason
      if (r === 'max_session') return this.$t('자동 종료(시간 초과)')
      if (r === 'idle')        return this.$t('자동 종료(무음 감지)')
      return ''
    },
    statusText() {
      if (this.vmState.error)            return this.$t('오류')
      if (this.vmState.isConnecting)     return this.$t('연결 중...')
      if (this.vmState.isRecording && this.isPaused) return this.$t('일시정지됨')
      if (this.vmState.isRecording && !this.vmState.liveReady) return this.$t('Live 준비 중...')
      if (this.vmState.isRecording)      return this.$t('실시간 통역 중')
      return this.$t('대기 중')
    },
    containerClasses() {
      return {
        'voice-container-mobile':     this.isMobile,
        'voice-container-fullscreen': !this.isMobile && this.fullScreen,
        'voice-container-history':    this.view !== 'main',
      }
    },
    headerTitle() {
      if (this.view === 'history-list')   return this.$t('녹음 히스토리')
      if (this.view === 'history-detail') return this.$t('녹음 상세')
      return this.$t('AI 통역 PM')
    },
    canOpenHistory() {
      return this.view === 'main' && !this.vmState.isRecording && !this.vmState.isConnecting
    },
    containerStyle() {
      if (this.isMobile) {
        return {}
      }
      if (this.fullScreen) {
        return {
          width:     'calc(100vw - 40px)',
          height:    'calc(100vh - 40px)',
          maxWidth:  'none',
          maxHeight: 'none',
        }
      }
      return {
        width:  '860px',
        height: '70vh',
      }
    },
  },

  watch: {
    'vmState.isRecording': function(val, prev) {
      if (val && !prev) {
        this.startTimer()
      } else if (!val && prev) {
        this.lastSessionDuration = this.recordingElapsed
        this.stopTimer()
      }
    },
    'vmState.turns.length': function(newLen, oldLen) {
      // 새 turn은 최상단에 추가됨 — 사용자가 아래로 스크롤한 상태가 아니면 상단으로
      if (newLen > oldLen) {
        if (this.userScrolledDown) {
          this.newTurnBadge += (newLen - oldLen)
        } else {
          this.$nextTick(this.scrollTurnsToTop)
        }
      } else if (newLen === 0) {
        this.userScrolledDown = false
        this.newTurnBadge     = 0
      }
    },
  },

  created() {
    var self = this
    this.voiceApi.setOnStateChange(function(newState) {
      self.vmState = newState
    })
    this.voiceApi.setOnEvent(function(evt) {
      self.handleVoiceEvent(evt)
    })
  },

  mounted() {
    this.checkMicPermission()
    this.loadBrowserVoices()
    window.addEventListener('resize', this.onResize)
    document.addEventListener('click', this.onDocumentClick)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
    document.removeEventListener('click', this.onDocumentClick)
    this.stopTimer()
    if (this.toastTimer) clearTimeout(this.toastTimer)
    this.stopPlayback()
  },

  methods: {
    onResize() {
      this.windowWidth = window.innerWidth
    },

    onDocumentClick(e) {
      // 언어 드롭다운 외부 클릭 시 닫기
      if (!e.target.closest || !e.target.closest('.voice-lang-picker')) {
        this.sourceOpen = false
        this.targetOpen = false
      }
    },

    checkMicPermission() {
      var self = this
      if (!navigator.permissions) {
        navigator.mediaDevices.getUserMedia({ audio: true })
          .then(function(stream) {
            stream.getTracks().forEach(function(t) { t.stop() })
            self.micPermission = 'granted'
          })
          .catch(function() { self.micPermission = 'prompt' })
        return
      }
      navigator.permissions.query({ name: 'microphone' }).then(function(result) {
        self.micPermission = result.state
        result.onchange = function() { self.micPermission = result.state }
      }).catch(function() { self.micPermission = 'prompt' })
    },

    async requestMicPermission() {
      try {
        var stream = await navigator.mediaDevices.getUserMedia({ audio: true })
        stream.getTracks().forEach(function(t) { t.stop() })
        this.micPermission = 'granted'
      } catch (e) {
        this.micPermission = 'denied'
      }
    },

    toggleSourceDropdown() {
      this.targetOpen = false
      this.sourceOpen = !this.sourceOpen
    },

    toggleTargetDropdown() {
      this.sourceOpen = false
      this.targetOpen = !this.targetOpen
    },

    selectSource(code) {
      // 동일 언어 선택 방지 (자동 교환)
      if (code === this.targetLang) {
        this.targetLang = this.sourceLang
      }
      this.sourceLang = code
      this.sourceOpen = false
    },

    selectTarget(code) {
      if (code === this.sourceLang) {
        this.sourceLang = this.targetLang
      }
      this.targetLang = code
      this.targetOpen = false
    },

    swapLanguages() {
      if (this.vmState.isRecording || this.vmState.isConnecting) return
      this.swapAnimating = true
      var tmp = this.sourceLang
      this.sourceLang = this.targetLang
      this.targetLang = tmp
      var self = this
      setTimeout(function() { self.swapAnimating = false }, 400)
    },

    async handleStart() {
      this.sessionSummary = null
      this.isPaused       = false
      // user_id / username은 서버가 JWT에서 추출 — 클라이언트가 보내지 않음
      await this.voiceApi.start({
        sourceLang: this.sourceLang,
        targetLang: this.targetLang,
      })
    },

    handlePause() {
      this.isPaused = true
      this.voiceApi.pause()
    },

    handleResume() {
      this.isPaused = false
      this.voiceApi.resume()
    },

    async handleStop() {
      this.isPaused   = false
      this.isStopping = true
      // 정지 후 state가 리셋되기 전에 recording_id 캡처
      var rid = this.vmState.recordingId
      try {
        var summary = await this.voiceApi.stop()
        if (summary) this.sessionSummary = summary
        if (rid) this.lastRecordingId = rid
      } finally {
        this.isStopping = false
      }
    },

    handleReset() {
      this.voiceApi.resetTexts()
      this.sessionSummary      = null
      this.lastSessionDuration = 0
      this.lastRecordingId     = null
      this.isPaused            = false
    },

    onClose() {
      if (this.vmState.isRecording) {
        this.voiceApi.stop()
      }
      if (window.speechSynthesis) window.speechSynthesis.cancel()
      this.$emit('close')
    },

    copyText(text) {
      if (!text || !navigator.clipboard) return
      var self = this
      navigator.clipboard.writeText(text).then(function() {
        self.showToast(self.$t('복사되었습니다'))
      }).catch(function(e) {
        console.warn('복사 실패:', e.message)
      })
    },

    copyTurn(turn) {
      var lines = []
      if (turn.original)   lines.push('[' + this.labelOf(turn.sourceLang) + '] ' + turn.original)
      if (turn.translated) lines.push('[' + this.labelOf(turn.targetLang) + '] ' + turn.translated)
      this.copyText(lines.join('\n'))
    },

    copyAllTurns() {
      if (!this.vmState.turns || this.vmState.turns.length === 0) return
      var self = this
      var blocks = this.vmState.turns.map(function(t) {
        var parts = []
        parts.push('[' + t.time + ']')
        if (t.original)   parts.push('  ' + self.labelOf(t.sourceLang) + ': ' + t.original)
        if (t.translated) parts.push('  ' + self.labelOf(t.targetLang) + ': ' + t.translated)
        return parts.join('\n')
      })
      this.copyText(blocks.join('\n\n'))
    },

    flagOf(code) {
      var l = this.langByCode[code]
      return l ? l.flag : ''
    },

    labelOf(code) {
      var l = this.langByCode[code]
      return l ? l.label : code
    },

    onTurnsScroll() {
      var el = this.$refs.turnsList
      if (!el) return
      // 상단 30px 이내면 자동 스크롤 유지 (최신 카드가 위에 있으므로)
      var nearTop = el.scrollTop < 30
      this.userScrolledDown = !nearTop
      if (nearTop) {
        this.newTurnBadge = 0
      }
    },

    scrollTurnsToTop(force) {
      var el = this.$refs.turnsList
      if (!el) return
      el.scrollTop = 0
      if (force) {
        this.userScrolledDown = false
        this.newTurnBadge     = 0
      }
    },

    // ── TTS ──────────────────────────────────────────────────
    loadBrowserVoices() {
      var self = this
      if (!window.speechSynthesis) return
      function refresh() {
        self.browserVoices = window.speechSynthesis.getVoices() || []
      }
      refresh()
      // 음성 명단은 비동기로 로드되므로 voiceschanged 이벤트 + 1초 후 한 번 더 시도
      window.speechSynthesis.addEventListener('voiceschanged', refresh)
      setTimeout(refresh, 1000)
    },

    isBrowserTTSSupported(langCode) {
      if (!window.speechSynthesis) return false
      if (!this.browserVoices || this.browserVoices.length === 0) return false
      var prefix = (langCode || '').split('-')[0].toLowerCase()
      if (!prefix) return false
      return this.browserVoices.some(function(v) {
        return v.lang && v.lang.split('-')[0].toLowerCase() === prefix
      })
    },

    isPlayingKey(key) {
      return this.ttsKey === key && this.ttsStatus === 'playing'
    },

    isLoadingKey(key) {
      return this.ttsKey === key && this.ttsStatus === 'loading'
    },

    stopPlayback() {
      // 브라우저 TTS 중단
      if (window.speechSynthesis) {
        try { window.speechSynthesis.cancel() } catch (e) { /* ignore */ }
      }
      // 서버 TTS 재생 중단
      if (this.ttsAudio) {
        try { this.ttsAudio.pause() } catch (e) { /* ignore */ }
        try { this.ttsAudio.src = '' } catch (e) { /* ignore */ }
        this.ttsAudio = null
      }
      if (this.ttsAudioBlobUrl) {
        try { URL.revokeObjectURL(this.ttsAudioBlobUrl) } catch (e) { /* ignore */ }
        this.ttsAudioBlobUrl = null
      }
      // 진행 중인 fetch 취소
      if (this.ttsAbortController) {
        try { this.ttsAbortController.abort() } catch (e) { /* ignore */ }
        this.ttsAbortController = null
      }
      this.ttsKey    = null
      this.ttsStatus = 'idle'
    },

    speakText(text, langCode, key) {
      if (!text) return
      if (!key) key = 'anon_' + langCode

      // 같은 버튼을 다시 누르면 정지
      if (this.ttsKey === key && (this.ttsStatus === 'playing' || this.ttsStatus === 'loading')) {
        this.stopPlayback()
        return
      }

      // 진행 중인 재생 정리 후 새 재생 시작
      this.stopPlayback()

      if (this.isBrowserTTSSupported(langCode)) {
        this._playBrowserTTS(text, langCode, key)
      } else {
        this._playServerTTS(text, langCode, key)
      }
    },

    _playBrowserTTS(text, langCode, key) {
      var self = this
      var synth = window.speechSynthesis
      var lang  = this.languages.find((l) => l.code === langCode)
      var bcp47 = lang ? lang.bcp47 : 'en-US'

      this.ttsKey    = key
      this.ttsStatus = 'playing'

      // Chrome 워크어라운드: cancel 직후 speak()는 큐가 갇히므로 약간 지연
      setTimeout(function() {
        try {
          if (synth.paused) synth.resume()
          var utter = new SpeechSynthesisUtterance(text)
          utter.lang = bcp47
          utter.rate = 1
          utter.onend = function() {
            if (self.ttsKey === key) {
              self.ttsKey    = null
              self.ttsStatus = 'idle'
            }
          }
          utter.onerror = function(e) {
            console.warn('[TTS] browser onerror:', e.error)
            if (self.ttsKey === key) {
              self.ttsKey    = null
              self.ttsStatus = 'idle'
            }
          }
          synth.speak(utter)
        } catch (e) {
          console.warn('[TTS] browser speak 실패:', e.message)
          self.ttsKey    = null
          self.ttsStatus = 'idle'
        }
      }, 80)
    },

    async _playServerTTS(text, langCode, key) {
      var self = this
      this.ttsKey    = key
      this.ttsStatus = 'loading'

      var controller = new AbortController()
      this.ttsAbortController = controller

      try {
        // 1) TTS API 호출 → audioUrl 획득
        var ttsResponse = await this.$store.dispatch('miras/TEXT_TO_SPEECH', {
          text:  text,
          lang:  langCode,
          voice: 'nova',
        })

        if (controller.signal.aborted) return
        if (!ttsResponse || !ttsResponse.audioUrl) {
          throw new Error('audioUrl 없음')
        }

        // 2) 인증된 오디오 데이터 로드 (Blob)
        var audioUrl = ttsResponse.audioUrl
        var blobUrl
        if (audioUrl.indexOf('/api/v1/file') >= 0) {
          var resp = await this.$http.get(audioUrl, {
            responseType: 'blob',
            timeout: 30000,
            signal: controller.signal,
          })
          if (controller.signal.aborted) return
          if (!resp || !resp.data || resp.data.size === 0) {
            throw new Error('오디오 데이터 비어있음')
          }
          var blob = new Blob([resp.data], { type: 'audio/mpeg' })
          blobUrl = URL.createObjectURL(blob)
          this.ttsAudioBlobUrl = blobUrl
        } else {
          blobUrl = audioUrl
        }

        if (controller.signal.aborted) return

        // 3) 재생
        var audio = new Audio(blobUrl)
        this.ttsAudio = audio
        audio.onplaying = function() {
          if (self.ttsKey === key) self.ttsStatus = 'playing'
        }
        audio.onended = function() {
          if (self.ttsKey === key) {
            self.ttsKey    = null
            self.ttsStatus = 'idle'
          }
          if (self.ttsAudioBlobUrl === blobUrl) {
            try { URL.revokeObjectURL(blobUrl) } catch (e) { /* ignore */ }
            self.ttsAudioBlobUrl = null
          }
        }
        audio.onerror = function() {
          console.warn('[TTS] audio.onerror')
          if (self.ttsKey === key) {
            self.ttsKey    = null
            self.ttsStatus = 'idle'
          }
        }
        await audio.play()
      } catch (e) {
        if (e && (e.name === 'AbortError' || e.name === 'CanceledError')) return
        console.warn('[TTS] 서버 TTS 실패:', e.message)
        if (this.ttsKey === key) {
          this.ttsKey    = null
          this.ttsStatus = 'idle'
        }
        this.showToast(this.$t('음성 재생 실패'))
      } finally {
        if (this.ttsAbortController === controller) {
          this.ttsAbortController = null
        }
      }
    },

    showToast(msg, duration) {
      this.toastMessage = msg
      if (this.toastTimer) clearTimeout(this.toastTimer)
      var self = this
      var ms = duration ? duration : 1800
      this.toastTimer = setTimeout(function() { self.toastMessage = '' }, ms)
    },

    handleVoiceEvent(evt) {
      if (!evt) return
      if (evt.type === 'session_warning') {
        var sec = Math.round(evt.remainingMs / 1000)
        if (sec >= 60) {
          var min = Math.round(sec / 60)
          this.showToast(this.$t('{count}분 후 자동 종료됩니다', { count: min }), 3500)
        } else {
          this.showToast(this.$t('{count}초 후 자동 종료됩니다', { count: sec }), 3500)
        }
        return
      }
      if (evt.type === 'idle_warning') {
        var s = Math.round(evt.remainingMs / 1000)
        this.showToast(this.$t('{count}초간 음성이 없으면 자동 종료됩니다', { count: s }), 3500)
        return
      }
      if (evt.type === 'auto_stop') {
        // composable이 이미 stop()을 호출했음 — summary만 수신
        this.isStopping = false
        this.isPaused   = false
        if (evt.summary) {
          this.sessionSummary = evt.summary
        }
        // recording_id는 state 리셋 전 캡처되어 있을 수 있음
        if (this.vmState.recordingId) {
          this.lastRecordingId = this.vmState.recordingId
        }
        var msg = evt.reason === 'max_session'
          ? this.$t('최대 녹음 시간에 도달하여 자동 종료되었습니다')
          : this.$t('음성이 감지되지 않아 자동 종료되었습니다')
        this.showToast(msg, 4500)
        return
      }
    },

    startTimer() {
      this.stopTimer()
      this.recordingStartTime = Date.now()
      this.recordingElapsed   = 0
      var self = this
      this.timerInterval = setInterval(function() {
        self.recordingElapsed = Math.floor((Date.now() - self.recordingStartTime) / 1000)
      }, 1000)
    },

    stopTimer() {
      if (this.timerInterval) {
        clearInterval(this.timerInterval)
        this.timerInterval = null
      }
    },

    formatTime(sec) {
      if (!sec || sec < 0) sec = 0
      var m = Math.floor(sec / 60)
      var s = sec % 60
      return (m < 10 ? '0' + m : '' + m) + ':' + (s < 10 ? '0' + s : '' + s)
    },

    // ── 히스토리 ──────────────────────────────────────────────
    isValidUUID(id) {
      return /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(id || '')
    },

    openHistory() {
      if (!this.canOpenHistory) return
      // 진행 중인 TTS 정리
      this.stopPlayback()
      this.view = 'history-list'
      // 매번 최신 상태 확인을 위해 1페이지부터 다시 로드
      this.loadHistoryList(1)
    },

    async loadHistoryList(page) {
      this.historyLoading = true
      this.historyError   = null
      try {
        var res = await this.$store.dispatch('miras/VOICE_RECORDINGS_LIST', {
          page:  page,
          limit: this.historyLimit,
        })
        var d = res && res.data ? res.data : res
        this.historyList       = d.recordings ? d.recordings : []
        this.historyTotal      = d.total      ? d.total      : 0
        this.historyPage       = d.page       ? d.page       : page
        this.historyTotalPages = d.total_pages ? d.total_pages : 0
      } catch (e) {
        this.historyError = this._extractApiError(e, this.$t('히스토리를 불러오지 못했습니다'))
      } finally {
        this.historyLoading = false
      }
    },

    openHistoryDetail(recordingId) {
      if (!this.isValidUUID(recordingId)) {
        this.showToast(this.$t('잘못된 녹음 ID입니다'), 3000)
        return
      }
      this.stopPlayback()
      this.view = 'history-detail'
      this.loadHistoryDetail(recordingId)
    },

    async loadHistoryDetail(recordingId) {
      this.detailLoading = true
      this.detailError   = null
      this.detailData    = null
      try {
        var res = await this.$store.dispatch('miras/VOICE_RECORDING_DETAIL', {
          recordingId: recordingId,
        })
        var d = res && res.data ? res.data : res
        // 응답에 turns가 있는지 검증
        if (!d || !d.recording_id) {
          throw new Error('invalid response')
        }
        if (!d.turns) d.turns = []
        this.detailData = d
      } catch (e) {
        this.detailError = this._extractApiError(e, this.$t('녹음 상세를 불러오지 못했습니다'))
      } finally {
        this.detailLoading = false
      }
    },

    _extractApiError(e, fallback) {
      if (!e) return fallback
      if (e.response && e.response.data) {
        var d = e.response.data
        if (d.error)   return d.error
        if (d.message) return d.message
      }
      if (e.message) {
        if (/network|timeout|aborted/i.test(e.message)) return this.$t('네트워크 오류')
      }
      return fallback
    },

    goBack() {
      this.stopPlayback()
      if (this.view === 'history-detail') {
        this.view = 'history-list'
        this.detailData  = null
        this.detailError = null
      } else if (this.view === 'history-list') {
        this.view = 'main'
      }
    },

    copyDetailTurn(turn) {
      var lines = []
      if (turn.stt_text) {
        var srcLang = turn.source_language ? turn.source_language : (this.detailData ? this.detailData.source_language : '')
        lines.push('[' + this.labelOf(srcLang) + '] ' + turn.stt_text)
      }
      if (turn.translated_text) {
        var tgtLang = turn.target_language ? turn.target_language : (this.detailData ? this.detailData.target_language : '')
        lines.push('[' + this.labelOf(tgtLang) + '] ' + turn.translated_text)
      }
      this.copyText(lines.join('\n'))
    },

    copyDetailAll() {
      if (!this.detailData || !this.detailData.turns) return
      var self = this
      var blocks = this.detailData.turns.map(function(t, idx) {
        var parts = ['#' + (idx + 1) + ' [' + self.formatTurnTime(t.created_at) + ']']
        if (t.stt_text) {
          var srcLang = t.source_language ? t.source_language : self.detailData.source_language
          parts.push('  ' + self.labelOf(srcLang) + ': ' + t.stt_text)
        }
        if (t.translated_text) {
          var tgtLang = t.target_language ? t.target_language : self.detailData.target_language
          parts.push('  ' + self.labelOf(tgtLang) + ': ' + t.translated_text)
        }
        return parts.join('\n')
      })
      this.copyText(blocks.join('\n\n'))
    },

    // ── 날짜/시간 포맷 ────────────────────────────────────────
    formatHistoryTime(iso) {
      if (!iso) return ''
      var d = new Date(iso)
      if (isNaN(d.getTime())) return iso
      var now = new Date()
      var diff = now - d
      var min = Math.floor(diff / 60000)
      if (min < 1)  return this.$t('방금 전')
      if (min < 60) return this.$t('n분 전', { count: min })
      var hr = Math.floor(min / 60)
      if (hr < 24)  return this.$t('n시간 전', { count: hr })
      var day = Math.floor(hr / 24)
      if (day < 7)  return this.$t('n일 전', { count: day })
      // 7일 이상 — 절대 시각
      return d.getFullYear() + '-' + this._pad2(d.getMonth() + 1) + '-' + this._pad2(d.getDate()) + ' ' +
             this._pad2(d.getHours()) + ':' + this._pad2(d.getMinutes())
    },

    formatHistoryDuration(rec) {
      // total_audio_seconds 우선, 없으면 ended_at - started_at
      var sec = rec.total_audio_seconds
      if (!sec && rec.started_at && rec.ended_at) {
        var s = new Date(rec.started_at).getTime()
        var e = new Date(rec.ended_at).getTime()
        if (!isNaN(s) && !isNaN(e) && e > s) sec = (e - s) / 1000
      }
      if (!sec) return '--:--'
      return this.formatTime(Math.round(sec))
    },

    formatTurnTime(iso) {
      if (!iso) return ''
      var d = new Date(iso)
      if (isNaN(d.getTime())) return ''
      return this._pad2(d.getHours()) + ':' + this._pad2(d.getMinutes()) + ':' + this._pad2(d.getSeconds())
    },

    _pad2(n) {
      return n < 10 ? '0' + n : '' + n
    },
  },
}
</script>

<style scoped>
/* ── 디자인 토큰 ─────────────────────────────────────────── */
.voice-root {
  --vp: #1E0D61;
  --vp-hover: #2d1a8e;
  --vd: #E53E3E;
  --vd-hover: #c53030;
  --vw: #F59E0B;
  --vs: #10B981;
  --bg: #ffffff;
  --bg-subtle: #F9FAFB;
  --bg-muted: #F3F4F6;
  --border: #E5E7EB;
  --border-strong: #D1D5DB;
  --text: #111827;
  --text-muted: #6B7280;
  --text-faint: #9CA3AF;
  --radius: 14px;
  --radius-sm: 8px;
  --shadow-lg: 0 10px 40px rgba(0,0,0,0.18);
  --shadow-md: 0 4px 12px rgba(0,0,0,0.08);
}

/* ── 백드롭 ──────────────────────────────────────────────── */
.voice-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.45);
  backdrop-filter: blur(2px);
  z-index: 9998;
}

/* ── 컨테이너 (항상 화면 중앙 고정) ────────────────────────── */
.voice-container {
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 9999;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-lg);
  display: grid;
  grid-template-rows: auto auto 1fr;
  overflow: hidden;
  max-width: calc(100vw - 24px);
  max-height: calc(100vh - 24px);
  color: var(--text);
  font-family: inherit;
}

/* 히스토리 뷰: 헤더 + 본문(1fr) — 2행 레이아웃 */
.voice-container.voice-container-history {
  grid-template-rows: auto 1fr;
}

/* 모바일 */
.voice-container-mobile {
  width: calc(100vw - 24px) !important;
  max-width: 480px;
  height: 86vh !important;
  max-height: 720px;
}

/* ── 헤더 ────────────────────────────────────────────────── */
.voice-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--border);
  background: linear-gradient(to bottom, #ffffff, #fafafa);
}
.voice-header-left {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
}
.voice-header-title {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.voice-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  line-height: 1.2;
}
.voice-subtitle {
  font-size: 11px;
  color: var(--text-muted);
  line-height: 1.4;
  margin-top: 2px;
}
.voice-header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}
.voice-timer {
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--vd);
  background: #FEE2E2;
  padding: 4px 8px;
  border-radius: 6px;
  margin-right: 6px;
  transition: background 0.2s, color 0.2s;
}
.voice-timer.is-warning {
  background: #FEF3C7;
  color: #B45309;
}
.voice-timer.is-critical {
  background: var(--vd);
  color: #fff;
  animation: voice-pulse-strong 0.9s ease-in-out infinite;
}
@keyframes voice-pulse-strong {
  0%, 100% { box-shadow: 0 0 0 0 rgba(229,62,62,0.6); }
  50%      { box-shadow: 0 0 0 6px rgba(229,62,62,0); }
}

/* 상태 도트 */
.voice-status-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  position: relative;
}
.voice-status-dot.is-idle       { background: var(--text-faint); }
.voice-status-dot.is-connecting { background: var(--vw); animation: voice-pulse 1.4s ease-in-out infinite; }
.voice-status-dot.is-preparing  { background: var(--vw); animation: voice-pulse 1.4s ease-in-out infinite; }
.voice-status-dot.is-recording  { background: var(--vd); animation: voice-pulse 1.2s ease-in-out infinite; }
.voice-status-dot.is-paused     { background: var(--vw); }
.voice-status-dot.is-error      { background: var(--vd); }

@keyframes voice-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(229,62,62,0.5); }
  50%      { box-shadow: 0 0 0 6px rgba(229,62,62,0); }
}

/* ── 권한 영역 ──────────────────────────────────────────── */
.voice-permission {
  padding: 28px 24px;
  background: #FFFBEB;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;
  border-bottom: 1px solid var(--border);
}
.voice-permission-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #FEF3C7;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #B45309;
  margin-bottom: 4px;
}
.voice-permission-icon >>> svg { width: 26px; height: 26px; }
.voice-permission-title {
  font-size: 14px;
  font-weight: 700;
  color: #92400E;
  margin: 0;
}
.voice-permission-text {
  font-size: 12px;
  color: var(--text-muted);
  margin: 0;
  max-width: 320px;
  line-height: 1.5;
}

/* ── 제어 영역 ──────────────────────────────────────────── */
.voice-controls {
  padding: 14px 16px 12px;
  background: var(--bg-subtle);
  border-bottom: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* 언어 선택기 */
.voice-lang-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 8px;
  align-items: center;
}
.voice-lang-picker {
  position: relative;
  min-width: 0;
}
.voice-lang-picker.is-disabled .voice-lang-btn { opacity: 0.6; cursor: not-allowed; }

.voice-lang-btn {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 9px 12px;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  cursor: pointer;
  font-size: 13px;
  color: var(--text);
  transition: all 0.15s;
  text-align: left;
}
.voice-lang-btn:hover:not(:disabled) {
  border-color: var(--vp);
  box-shadow: 0 0 0 2px rgba(30,13,97,0.08);
}
.voice-lang-picker.is-open .voice-lang-btn {
  border-color: var(--vp);
  box-shadow: 0 0 0 2px rgba(30,13,97,0.12);
}
.voice-lang-flag {
  font-size: 18px;
  line-height: 1;
  flex-shrink: 0;
}
.voice-lang-name {
  flex: 1;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.voice-lang-caret {
  width: 14px !important;
  height: 14px !important;
  color: var(--text-muted);
  flex-shrink: 0;
  transition: transform 0.2s;
}
.voice-lang-picker.is-open .voice-lang-caret {
  transform: rotate(180deg);
}

/* 드롭다운 패널 */
.voice-lang-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-md);
  z-index: 100;
  max-height: 280px;
  overflow-y: auto;
  padding: 4px;
}
.voice-lang-option {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  text-align: left;
  font-size: 13px;
  transition: background 0.1s;
}
.voice-lang-option:hover { background: var(--bg-muted); }
.voice-lang-option.is-active { background: rgba(30,13,97,0.06); }
.voice-lang-option-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.voice-lang-option-label {
  font-weight: 500;
  color: var(--text);
}
.voice-lang-option-sub {
  font-size: 11px;
  color: var(--text-faint);
}
.voice-lang-check {
  width: 14px !important;
  height: 14px !important;
  color: var(--vp);
}

/* Swap 버튼 */
.voice-swap-btn {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: 1px solid var(--border);
  background: var(--bg);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--vp);
  transition: all 0.2s;
  flex-shrink: 0;
}
.voice-swap-btn:hover:not(:disabled) {
  background: var(--vp);
  color: #fff;
  border-color: var(--vp);
  transform: scale(1.05);
}
.voice-swap-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.voice-swap-btn.is-swapping { animation: voice-spin-half 0.4s ease-in-out; }
.voice-swap-btn >>> svg { width: 16px; height: 16px; }

@keyframes voice-spin-half {
  from { transform: rotate(0deg); }
  to   { transform: rotate(180deg); }
}

/* 컨트롤 버튼 */
.voice-action-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.voice-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 9px 16px;
  border-radius: var(--radius-sm);
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.15s;
  white-space: nowrap;
}
.voice-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.voice-btn-main { flex: 1; }
.voice-btn-primary {
  background: var(--vp);
  color: #fff;
}
.voice-btn-primary:hover:not(:disabled) { background: var(--vp-hover); }
.voice-btn-danger {
  background: var(--vd);
  color: #fff;
}
.voice-btn-danger:hover:not(:disabled) { background: var(--vd-hover); }
.voice-btn-outline {
  background: transparent;
  color: var(--text);
  border: 1px solid var(--border-strong);
}
.voice-btn-outline:hover:not(:disabled) { background: var(--bg-muted); }

.voice-action-reset { margin-left: auto; }

/* 아이콘 버튼 */
.voice-icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  transition: all 0.15s;
}
.voice-icon-btn:hover:not(:disabled) {
  background: var(--bg-muted);
  color: var(--text);
}
.voice-icon-btn:disabled { opacity: 0.3; cursor: not-allowed; }
.voice-icon { width: 16px; height: 16px; }
.voice-spin { animation: voice-spin 1s linear infinite; }
@keyframes voice-spin {
  from { transform: rotate(0deg); }
  to   { transform: rotate(360deg); }
}

/* 오류 */
.voice-error {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: #FEF2F2;
  border: 1px solid #FECACA;
  border-radius: var(--radius-sm);
  color: var(--vd);
  font-size: 12px;
}
.voice-error-text { flex: 1; }
.voice-error-retry {
  background: var(--vd);
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}
.voice-error-retry:hover { background: var(--vd-hover); }

/* ── 결과 영역 ──────────────────────────────────────────── */
.voice-result {
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 0;
  background: var(--bg-subtle);
}

/* ── 카드 리스트 ────────────────────────────────────────── */
.voice-turns-wrap {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  position: relative;
}
.voice-turns-list {
  flex: 1;
  overflow-y: auto;
  padding: 14px 14px 18px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  scroll-behavior: smooth;
}

/* 개별 turn 카드 */
.voice-turn-card {
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 10px 12px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.03);
  transition: border-color 0.15s, box-shadow 0.15s;
}
.voice-turn-card:hover {
  border-color: var(--border-strong);
  box-shadow: 0 2px 6px rgba(0,0,0,0.06);
}
.voice-turn-card-live {
  border-color: var(--vd);
  background: linear-gradient(to bottom, #FFF5F5, var(--bg) 40%);
  box-shadow: 0 0 0 2px rgba(229,62,62,0.08);
}

.voice-turn-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.voice-turn-time {
  font-size: 11px;
  font-weight: 600;
  color: var(--text-faint);
  font-variant-numeric: tabular-nums;
}
.voice-turn-index {
  font-size: 11px;
  color: var(--text-faint);
  padding: 1px 6px;
  background: var(--bg-muted);
  border-radius: 10px;
}
.voice-turn-live-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  color: var(--vd);
  font-weight: 700;
}
.voice-live-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: var(--vd);
  animation: voice-pulse 1.2s ease-in-out infinite;
}
.voice-turn-action {
  margin-left: auto;
  width: 26px;
  height: 26px;
  opacity: 0;
  transition: opacity 0.15s;
}
.voice-turn-card:hover .voice-turn-action {
  opacity: 1;
}

/* turn 내부 row */
.voice-turn-row {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 10px;
  align-items: start;
  padding: 4px 0;
}
.voice-turn-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 8px;
  background: var(--bg-muted);
  border-radius: 14px;
  font-size: 10px;
  font-weight: 600;
  color: var(--text-muted);
  white-space: nowrap;
  margin-top: 2px;
  flex-shrink: 0;
}
.voice-turn-chip .voice-lang-flag {
  font-size: 13px;
}
.voice-turn-row-target .voice-turn-chip {
  background: rgba(30,13,97,0.08);
  color: var(--vp);
}
.voice-turn-text {
  font-size: 14px;
  line-height: 1.55;
  color: var(--text);
  margin: 0;
  word-break: break-word;
  white-space: pre-wrap;
}
.voice-turn-row-target .voice-turn-text {
  font-weight: 500;
}
.voice-turn-partial {
  color: var(--text-muted);
  font-style: italic;
}
.voice-turn-row-action {
  width: 26px;
  height: 26px;
  opacity: 0;
  transition: opacity 0.15s;
  flex-shrink: 0;
}
.voice-turn-card:hover .voice-turn-row-action,
.voice-turn-row-action.is-active {
  opacity: 1;
}
.voice-turn-row-action.is-active {
  background: var(--vp);
  color: #fff;
}
.voice-turn-row-action.is-active:hover {
  background: var(--vp-hover);
  color: #fff;
}
.voice-turn-divider {
  height: 1px;
  background: var(--border);
  margin: 4px 0;
}

/* 커서 */
.voice-cursor {
  display: inline-block;
  color: var(--vd);
  font-style: normal;
  animation: voice-blink 0.9s steps(2) infinite;
  margin-left: 1px;
}
@keyframes voice-blink {
  0%, 50%   { opacity: 1; }
  51%, 100% { opacity: 0; }
}

/* 세션 종료 배너 */
.voice-summary-banner {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  background: linear-gradient(to right, #ECFDF5, #F0FDF4);
  border-bottom: 1px solid #BBF7D0;
  flex-shrink: 0;
}
.voice-summary-banner-check {
  width: 18px !important;
  height: 18px !important;
  color: var(--vs);
}
.voice-summary-banner-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}
.voice-summary-banner-title {
  font-size: 13px;
  font-weight: 700;
  color: #065F46;
}
.voice-summary-banner-reason {
  font-weight: 500;
  color: #047857;
  margin-left: 2px;
}
.voice-summary-banner-meta {
  font-size: 11px;
  color: #047857;
  font-variant-numeric: tabular-nums;
}
.voice-summary-banner-action {
  width: 30px;
  height: 30px;
  color: #047857;
}
.voice-summary-banner-action:hover {
  background: rgba(16,185,129,0.12);
  color: #065F46;
}

/* 새 메시지 배지 (상단으로 복귀 안내) */
.voice-new-badge {
  position: absolute;
  top: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--vp);
  color: #fff;
  border: none;
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  box-shadow: var(--shadow-md);
  animation: voice-badge-in 0.25s;
}
.voice-new-badge:hover {
  background: var(--vp-hover);
}
@keyframes voice-badge-in {
  from { opacity: 0; transform: translateX(-50%) translateY(-6px); }
  to   { opacity: 1; transform: translateX(-50%) translateY(0); }
}

/* 빈 상태 */
.voice-empty {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 32px 24px;
  gap: 8px;
}
.voice-empty-icon {
  width: 76px;
  height: 76px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(30,13,97,0.08), rgba(30,13,97,0.02));
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--vp);
  margin-bottom: 8px;
  animation: voice-soft-pulse 2.4s ease-in-out infinite;
}
.voice-empty-icon >>> svg { width: 32px; height: 32px; }
@keyframes voice-soft-pulse {
  0%, 100% { transform: scale(1);    box-shadow: 0 0 0 0 rgba(30,13,97,0.08); }
  50%      { transform: scale(1.03); box-shadow: 0 0 0 12px rgba(30,13,97,0); }
}
.voice-empty-title {
  font-size: 16px;
  font-weight: 700;
  color: var(--text);
  margin: 0;
}
.voice-empty-desc {
  font-size: 13px;
  color: var(--text-muted);
  margin: 0;
  max-width: 320px;
  line-height: 1.5;
}
.voice-empty-chip {
  margin-top: 12px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 14px;
  background: var(--bg-muted);
  border-radius: 24px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text-muted);
}
.voice-empty-chip .voice-lang-flag { font-size: 16px; }
.voice-empty-arrow {
  width: 14px !important;
  height: 14px !important;
  color: var(--text-faint);
}
.voice-empty-policy {
  margin: 18px 0 0;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  color: var(--text-faint);
  line-height: 1.4;
}
.voice-empty-policy-icon {
  width: 12px !important;
  height: 12px !important;
}


/* ── 토스트 ──────────────────────────────────────────────── */
.voice-toast {
  position: absolute;
  bottom: 18px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(17,24,39,0.92);
  color: #fff;
  padding: 8px 14px;
  border-radius: 20px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: var(--shadow-md);
  z-index: 200;
}
.voice-toast .voice-icon { color: var(--vs); }
.voice-toast-enter-active, .voice-toast-leave-active { transition: all 0.25s; }
.voice-toast-enter, .voice-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(6px);
}

/* ── 헤더 뒤로 가기 버튼 ─────────────────────────────────── */
.voice-back-btn {
  width: 30px;
  height: 30px;
  border: 1px solid var(--border);
  background: var(--bg);
  color: var(--text);
}
.voice-back-btn:hover {
  background: var(--bg-muted);
  border-color: var(--border-strong);
}

/* ── 세션 종료 배너 링크 ─────────────────────────────────── */
.voice-summary-banner-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  background: rgba(16,185,129,0.12);
  color: #065F46;
  border: 1px solid #6EE7B7;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.voice-summary-banner-link:hover {
  background: rgba(16,185,129,0.22);
  border-color: var(--vs);
}
.voice-summary-banner-link .voice-icon {
  width: 12px;
  height: 12px;
}

/* ── 히스토리 뷰 컨테이너 ────────────────────────────────── */
.voice-history {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: var(--bg-subtle);
  min-height: 0;
}
.voice-history-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
}

/* 메타 (총 N건) */
.voice-history-meta {
  flex-shrink: 0;
  padding: 10px 16px;
  font-size: 11px;
  color: var(--text-muted);
  border-bottom: 1px solid var(--border);
  background: var(--bg);
}

/* 목록 카드 */
.voice-history-cards {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.voice-history-card {
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 12px 14px;
  cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.1s;
}
.voice-history-card:hover {
  border-color: var(--vp);
  box-shadow: 0 2px 8px rgba(30,13,97,0.08);
}
.voice-history-card:active {
  transform: scale(0.998);
}

.voice-history-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}
.voice-history-card-time {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  color: var(--text);
}
.voice-history-card-duration {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
}
.voice-history-card-icon {
  width: 12px !important;
  height: 12px !important;
}

.voice-history-card-langs {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  flex-wrap: wrap;
}
.voice-history-lang {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  background: var(--bg-muted);
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-muted);
}
.voice-history-lang .voice-lang-flag {
  font-size: 13px;
}
.voice-history-arrow {
  width: 12px !important;
  height: 12px !important;
  color: var(--text-faint);
}
.voice-history-turn-badge {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  background: rgba(30,13,97,0.08);
  color: var(--vp);
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.voice-history-preview {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding-top: 6px;
  border-top: 1px dashed var(--border);
}
.voice-history-preview-line {
  margin: 0;
  font-size: 12px;
  color: var(--text-muted);
  line-height: 1.5;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  word-break: break-word;
}
.voice-history-preview-target {
  color: var(--text);
  font-weight: 500;
}

/* 페이지네이션 */
.voice-history-pagination {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 10px 12px;
  border-top: 1px solid var(--border);
  background: var(--bg);
}
.voice-history-page-info {
  font-size: 12px;
  color: var(--text-muted);
  font-variant-numeric: tabular-nums;
  font-weight: 600;
  min-width: 60px;
  text-align: center;
}

/* 빈 상태 / 에러 / 로딩 */
.voice-history-empty,
.voice-history-error {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 24px;
  gap: 10px;
}
.voice-history-empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: var(--bg-muted);
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-faint);
  margin-bottom: 6px;
}
.voice-history-empty-icon >>> svg {
  width: 28px;
  height: 28px;
}
.voice-history-empty-title {
  font-size: 15px;
  font-weight: 700;
  color: var(--text);
  margin: 0;
}
.voice-history-empty-desc {
  font-size: 12px;
  color: var(--text-muted);
  margin: 0;
  max-width: 280px;
  line-height: 1.5;
}
.voice-history-error-icon {
  width: 36px !important;
  height: 36px !important;
  color: var(--vd);
}
.voice-history-error-text {
  font-size: 13px;
  color: var(--text);
  margin: 0 0 8px;
  max-width: 320px;
  line-height: 1.5;
}

/* 로딩 스켈레톤 */
.voice-history-loading {
  flex: 1;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow: hidden;
}
.voice-history-skeleton {
  background: var(--bg);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.voice-skeleton-line {
  height: 12px;
  background: linear-gradient(90deg, var(--bg-muted), var(--bg-subtle), var(--bg-muted));
  background-size: 200% 100%;
  border-radius: 4px;
  animation: voice-skeleton-shimmer 1.4s linear infinite;
}
.voice-skeleton-line-1 { width: 45%; }
.voice-skeleton-line-2 { width: 70%; }
.voice-skeleton-line-3 { width: 90%; }
@keyframes voice-skeleton-shimmer {
  from { background-position: 200% 0; }
  to   { background-position: -200% 0; }
}

/* 상세 헤더 */
.voice-history-detail-header {
  flex-shrink: 0;
  padding: 14px 18px;
  background: var(--bg);
  border-bottom: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.voice-history-detail-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: var(--text-muted);
  flex-wrap: wrap;
}
.voice-history-detail-meta > span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.voice-history-detail-divider {
  color: var(--border-strong);
}
.voice-history-detail-langs {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.voice-history-detail-copy {
  margin-left: auto;
}

/* 상세 turn 리스트 — 기존 turn 카드 스타일 재사용 */
.voice-history-turns {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.voice-turn-duration {
  font-size: 10px;
  color: var(--text-faint);
  font-variant-numeric: tabular-nums;
  padding: 1px 6px;
  background: var(--bg-muted);
  border-radius: 8px;
}
/* 상세 뷰에서는 카드 액션 버튼 항상 표시 */
.voice-history-turns .voice-turn-action,
.voice-history-turns .voice-turn-row-action {
  opacity: 1;
}

/* ── 모바일 반응형 미세조정 ─────────────────────────────── */
@media (max-width: 480px) {
  .voice-header { padding: 12px 14px; }
  .voice-controls { padding: 12px 12px 10px; }
  .voice-turns-list { padding: 12px 10px 16px; }
  .voice-turn-card { padding: 10px; }
  .voice-btn { padding: 10px 12px; font-size: 13px; }
  .voice-title { font-size: 14px; }
  /* 모바일에선 action 버튼 항상 표시 (hover 미지원) */
  .voice-turn-action,
  .voice-turn-row-action { opacity: 1; }
  /* 히스토리 */
  .voice-history-cards,
  .voice-history-turns { padding: 10px; }
  .voice-history-card { padding: 10px 12px; }
}
</style>
